import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Sparkles, ArrowLeft, Calendar, Users, Share2, Check, Copy } from 'lucide-react'
import { getNewsletterById, formatNewsletterDate } from '../utils/newsletter'

export default function NewsletterViewPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [newsletter, setNewsletter] = useState(null)
    const [loading, setLoading] = useState(true)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        async function fetchNewsletter() {
            if (!id) {
                navigate('/newsletter')
                return
            }
            const data = await getNewsletterById(id)
            if (!data) {
                navigate('/newsletter')
                return
            }
            setNewsletter(data)
            setLoading(false)
        }
        fetchNewsletter()
    }, [id, navigate])

    const handleShare = async () => {
        const url = window.location.href
        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-primary-400 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-950">
            {/* Header */}
            <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-slate-950/80 backdrop-blur-xl z-50">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">InkSpot</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleShare}
                            className="btn-ghost text-sm"
                        >
                            {copied ? (
                                <>
                                    <Check className="w-4 h-4 mr-2 text-green-400" />
                                    Copied!
                                </>
                            ) : (
                                <>
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share
                                </>
                            )}
                        </button>
                        <Link to="/newsletter" className="btn-ghost text-sm">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            All Issues
                        </Link>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-6 py-12">
                {/* Newsletter Header */}
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                        {newsletter.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-white/50">
                        <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {formatNewsletterDate(newsletter.published_at)}
                        </span>
                        {newsletter.recipient_count > 0 && (
                            <span className="flex items-center gap-1.5">
                                <Users className="w-4 h-4" />
                                Sent to {newsletter.recipient_count.toLocaleString()} subscribers
                            </span>
                        )}
                    </div>
                </div>

                {/* Newsletter Content */}
                <div className="glass-card overflow-hidden">
                    <iframe
                        srcDoc={newsletter.content_html}
                        title={newsletter.title}
                        className="w-full min-h-[800px] border-0"
                        style={{ background: '#0f172a' }}
                    />
                </div>

                {/* Navigation */}
                <div className="mt-8 flex items-center justify-between">
                    <Link to="/newsletter" className="btn-ghost">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Archive
                    </Link>
                    <Link to="/#newsletter" className="btn-primary">
                        Subscribe for Updates
                    </Link>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 mt-12">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <p className="text-white/40 text-sm">
                        © {new Date().getFullYear()} InkSpot. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    )
}
